import { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { useModal } from "../../context/ModalContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const PAGE_SIZE = 20;

export default function AdminGallery() {
  const { showAlert, showConfirm } = useModal();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      setLoading(true);
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from("gallery")
        .select("*, profiles(username)", { count: "exact" });

      if (tab === "all") {
        query = query.not("status", "eq", "deleted");
      } else {
        query = query.eq("status", tab);
      }

      if (search.trim()) {
        query = query.or(`title.ilike.%${search.trim()}%,tags.cs.{${search.trim()}}`);
      }

      query = query.order("created_at", { ascending: false }).range(from, to);

      const { data, error, count } = await query;
      if (!cancelled && !error) {
        setPosts(data || []);
        setTotal(count || 0);
      }
      if (!cancelled) setLoading(false);
    }

    fetchPosts();

    return () => { cancelled = true; };
  }, [tab, page, search]);

  useEffect(() => {
    setSelectedIds([]);
  }, [tab, page]);

  async function callGalleryStatus(postIds, status, rejectReason) {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${API_BASE}/admin/update-gallery-status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ post_ids: postIds, status, reject_reason: rejectReason }),
    });
    return res.ok;
  }

  async function updateStatus(postId, status) {
    const ok = await callGalleryStatus([postId], status);
    if (!ok) { showAlert("상태 변경 실패"); return; }
    setPosts(posts.map((p) => (p.id === postId ? { ...p, status } : p)));
  }

  async function hidePost(postId) {
    const ok = await callGalleryStatus([postId], "hidden");
    if (!ok) { showAlert("숨기기 실패"); return; }
    setPosts(posts.map((p) => p.id === postId ? { ...p, status: "hidden" } : p));
  }

  async function unhidePost(postId) {
    const ok = await callGalleryStatus([postId], "approved");
    if (!ok) { showAlert("숨기기 해제 실패"); return; }
    setPosts(posts.map((p) => p.id === postId ? { ...p, status: "approved" } : p));
  }

  async function softDeletePost(postId) {
    if (!(await showConfirm("삭제하시겠습니까? 30일 후 자동으로 영구 삭제됩니다."))) return;
    const ok = await callGalleryStatus([postId], "deleted");
    if (!ok) { showAlert("삭제 실패"); return; }
    setPosts(posts.filter((p) => p.id !== postId));
    setTotal((prev) => prev - 1);
  }

  async function restorePost(postId) {
    if (!(await showConfirm("이 게시글을 복원하시겠습니까?"))) return;
    const ok = await callGalleryStatus([postId], "approved");
    if (!ok) { showAlert("복원 실패"); return; }
    setPosts(posts.filter((p) => p.id !== postId));
    setTotal((prev) => prev - 1);
  }

  async function permanentDeletePost(postId) {
    if (!(await showConfirm("영구 삭제하시겠습니까? 복원할 수 없습니다."))) return;
    const { error } = await supabase.functions.invoke("delete-gallery-post", {
      body: { post_id: postId },
    });
    if (!error) {
      setPosts(posts.filter((p) => p.id !== postId));
      setTotal((prev) => prev - 1);
    } else {
      showAlert("영구 삭제 실패");
    }
  }

  async function bulkUpdateStatus(status) {
    if (!(await showConfirm(`선택한 ${selectedIds.length}개 게시글을 ${status === "approved" ? "승인" : "거절"}하시겠습니까?`))) return;
    const ok = await callGalleryStatus(selectedIds, status);
    if (!ok) { showAlert("상태 변경 실패"); return; }
    setPosts(posts.map((p) => selectedIds.includes(p.id) ? { ...p, status } : p));
    setSelectedIds([]);
  }

  async function bulkHide() {
    if (!(await showConfirm(`선택한 ${selectedIds.length}개 게시글을 숨기겠습니까?`))) return;
    const ok = await callGalleryStatus(selectedIds, "hidden");
    if (!ok) { showAlert("숨기기 실패"); return; }
    setPosts(posts.map((p) => selectedIds.includes(p.id) ? { ...p, status: "hidden" } : p));
    setSelectedIds([]);
  }

  async function bulkDelete() {
    if (!(await showConfirm(`선택한 ${selectedIds.length}개 게시글을 삭제하시겠습니까? 30일 후 자동으로 영구 삭제됩니다.`))) return;
    const ok = await callGalleryStatus(selectedIds, "deleted");
    if (!ok) { showAlert("삭제 실패"); return; }
    setPosts(posts.filter((p) => !selectedIds.includes(p.id)));
    setTotal((prev) => prev - selectedIds.length);
    setSelectedIds([]);
  }

  async function purgeExpired() {
    if (!(await showConfirm("30일이 지난 삭제 항목을 모두 영구 삭제하시겠습니까?"))) return;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${API_BASE}/admin/purge-expired-gallery`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) { showAlert("정리 실패"); return; }
    const json = await res.json();
    showAlert(`${json.purged}개 항목이 영구 삭제되었습니다.`);
    setPosts(posts.filter(() => false));
    setTotal(0);
  }

  async function handleReject() {
    if (!rejectModal) return;
    const ok = await callGalleryStatus([rejectModal], "rejected", rejectReason || null);
    if (!ok) { showAlert("거절 처리 실패"); return; }
    setPosts(
      posts.map((p) =>
        p.id === rejectModal
          ? { ...p, status: "rejected", reject_reason: rejectReason }
          : p
      )
    );
    setRejectModal(null);
    setRejectReason("");
  }

  function toggleSelect(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleSelectAll() {
    if (selectedIds.length === posts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(posts.map((p) => p.id));
    }
  }

  const statusBadge = (status) => {
    const map = {
      pending:  { cls: "yellow", label: "대기" },
      approved: { cls: "green",  label: "승인" },
      rejected: { cls: "red",    label: "거절" },
      hidden:   { cls: "gray",   label: "숨김" },
      deleted:  { cls: "red",    label: "삭제됨" },
    };
    const s = map[status] || map.pending;
    return <span className={`admin-badge ${s.cls}`}>{s.label}</span>;
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const tabs = [
    { key: "pending",  label: "대기" },
    { key: "approved", label: "승인" },
    { key: "rejected", label: "거절" },
    { key: "hidden",   label: "숨김" },
    { key: "deleted",  label: "휴지통" },
    { key: "all",      label: "전체" },
  ];

  const isTrash = tab === "deleted";

  return (
    <div className="admin-page">
      <h2>갤러리 관리</h2>

      {/* 탭 */}
      <div className="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`admin-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => { setTab(t.key); setPage(1); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 검색 */}
      <div className="admin-search-row">
        <input
          className="admin-search-input"
          placeholder="제목 또는 태그 검색"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {isTrash && (
          <button className="admin-btn danger" style={{ marginLeft: 8 }} onClick={purgeExpired}>
            만료된 항목 정리 (30일↑)
          </button>
        )}
      </div>

      {/* 일괄 액션 */}
      {selectedIds.length > 0 && (
        <div className="admin-bulk-actions">
          <span className="admin-bulk-count">{selectedIds.length}개 선택</span>
          {!isTrash && (
            <>
              <button className="admin-btn approve" onClick={() => bulkUpdateStatus("approved")}>
                일괄 승인
              </button>
              <button className="admin-btn reject" onClick={() => bulkUpdateStatus("rejected")}>
                일괄 거절
              </button>
              <button className="admin-btn" onClick={bulkHide}>
                일괄 숨기기
              </button>
              <button className="admin-btn danger" onClick={bulkDelete}>
                일괄 삭제
              </button>
            </>
          )}
        </div>
      )}

      {/* 테이블 */}
      <div className="admin-card">
        {loading ? (
          <div style={{ padding: 20, textAlign: "center" }}>로딩 중…</div>
        ) : posts.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#999" }}>
            게시글이 없습니다.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === posts.length && posts.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ width: 60 }}>썸네일</th>
                <th>제목</th>
                <th>작성자</th>
                <th>상태</th>
                {isTrash ? (
                  <>
                    <th>삭제일</th>
                    <th>영구삭제 예정</th>
                  </>
                ) : (
                  <th>등록일</th>
                )}
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => {
                const deletedAt = isTrash && p.reject_reason ? new Date(p.reject_reason) : null;
                const purgeDate = deletedAt ? new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000) : null;
                const isExpired = purgeDate && purgeDate < new Date();

                return (
                  <tr key={p.id} className={selectedIds.includes(p.id) ? "selected-row" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                      />
                    </td>
                    <td>
                      {p.thumbnail_url ? (
                        <img
                          src={p.thumbnail_url}
                          alt=""
                          style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                        />
                      ) : (
                        <div style={{ width: 48, height: 48, background: "#F4EADB", borderRadius: 6 }} />
                      )}
                    </td>
                    <td>
                      <a
                        href={`/gallery/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-link"
                      >
                        {p.title}
                      </a>
                    </td>
                    <td>{p.profiles?.username || "-"}</td>
                    <td>{statusBadge(p.status)}</td>
                    {isTrash ? (
                      <>
                        <td>{deletedAt ? deletedAt.toLocaleDateString() : "-"}</td>
                        <td style={{ color: isExpired ? "#ef4444" : undefined }}>
                          {purgeDate ? purgeDate.toLocaleDateString() : "-"}
                          {isExpired && " (만료)"}
                        </td>
                      </>
                    ) : (
                      <td>{p.created_at ? new Date(p.created_at).toLocaleDateString() : "-"}</td>
                    )}
                    <td>
                      <div className="admin-action-btns">
                        {isTrash ? (
                          <>
                            <button className="admin-btn approve" onClick={() => restorePost(p.id)}>
                              복원
                            </button>
                            <button className="admin-btn danger" onClick={() => permanentDeletePost(p.id)}>
                              영구삭제
                            </button>
                          </>
                        ) : (
                          <>
                            {p.status !== "approved" && p.status !== "hidden" && (
                              <button className="admin-btn approve" onClick={() => updateStatus(p.id, "approved")}>
                                승인
                              </button>
                            )}
                            {p.status === "hidden" && (
                              <button className="admin-btn approve" onClick={() => unhidePost(p.id)}>
                                숨기기 해제
                              </button>
                            )}
                            {p.status !== "rejected" && p.status !== "hidden" && (
                              <button
                                className="admin-btn reject"
                                onClick={() => { setRejectModal(p.id); setRejectReason(""); }}
                              >
                                거절
                              </button>
                            )}
                            {p.status !== "hidden" && (
                              <button className="admin-btn" onClick={() => hidePost(p.id)}>
                                숨기기
                              </button>
                            )}
                            <button className="admin-btn danger" onClick={() => softDeletePost(p.id)}>
                              삭제
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i + 1}
              className={`admin-page-btn ${page === i + 1 ? "active" : ""}`}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* 거절 사유 모달 */}
      {rejectModal && (
        <div className="admin-modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>거절 사유</h3>
            <textarea
              className="admin-modal-textarea"
              placeholder="거절 사유를 입력하세요 (선택)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="admin-modal-actions">
              <button className="admin-btn" onClick={() => setRejectModal(null)}>
                취소
              </button>
              <button className="admin-btn reject" onClick={handleReject}>
                거절 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
